// state/chat-sheet.tsx — V28 Phase F3 / C3.
//
// Global open/close state for the swipe-up "Chiedi a Il Prof" sheet. This file is
// JUST the state — the actual sheet UI (`GlobalChatSheet`) lands in Phase C3 and
// will consume `useChatSheet()`. Keeping the state separate means the swipe-up
// gesture (AppFrame), the `SwipeHint` button, and the future sheet all talk to
// one source of truth.
//
// The provider is mounted inside `AppFrame` so every student route shares one
// sheet instance. Not persisted — open/close is ephemeral UI state.

import { createContext, useContext, useState, useCallback, useMemo, type ReactNode } from 'react';

interface ChatSheetContextValue {
  isOpen: boolean;
  open: () => void;
  close: () => void;
}

const ChatSheetContext = createContext<ChatSheetContextValue | null>(null);

export function ChatSheetProvider({ children }: { children: ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);

  const open = useCallback(() => setIsOpen(true), []);
  const close = useCallback(() => setIsOpen(false), []);

  const value = useMemo<ChatSheetContextValue>(
    () => ({ isOpen, open, close }),
    [isOpen, open, close],
  );

  return <ChatSheetContext.Provider value={value}>{children}</ChatSheetContext.Provider>;
}

/**
 * Access the global chat-sheet state. Must be called inside `ChatSheetProvider`
 * (mounted by AppFrame).
 */
export function useChatSheet(): ChatSheetContextValue {
  const ctx = useContext(ChatSheetContext);
  if (!ctx) throw new Error('useChatSheet must be used inside ChatSheetProvider');
  return ctx;
}

/** Optional host for reusable reference cards rendered outside AppFrame. */
export function useOptionalChatSheet(): ChatSheetContextValue | null {
  return useContext(ChatSheetContext);
}
